import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Plus, Check, ListTodo, ChevronRight, Search } from 'lucide-react';
import api from '../../../api.js';
import { insuranceModuleAPI } from '../../../api/insurance.js';
import { buildTrulyInsuredIds, SINGLE_VEHICLE_CONTRACT_TYPES } from '../../../utils/insuranceStatus.js';

// Repli quand aucun coverage_model n'existe : le libelle vient de la traduction
// (nameKey), alors qu'un modele venant de la base porte son propre `name`.
const FALLBACK_COVERAGES = [
  { id: 'tous_risques', nameKey: 'insurance.picker.coverage.tous_risques', recommended: true },
  { id: 'tiers_plus',   nameKey: 'insurance.picker.coverage.tiers_plus' },
  { id: 'tiers',        nameKey: 'insurance.picker.coverage.tiers' },
];

/**
 * Unified "assign vehicles to an insurance contract" picker.
 * Used from ContractDetailModal / InsuranceUserView (contract fixed)
 * and BulkActionsModal (contract chosen inside the picker).
 *
 * Filter is GLOBAL: a vehicle with any active vehicle_insurance row
 * (removed_at IS NULL) on any contract is excluded. To move a vehicle
 * from one contract to another, terminate it from the old one first.
 */
function InsuranceVehiclePicker({ contract: fixedContract = null, onClose, onSuccess, onMessage }) {
  const { t } = useTranslation();
  // Un modele venant de la base porte son `name` ; un repli porte sa cle.
  const coverageName = (c) => (c?.nameKey ? t(c.nameKey) : c?.name);
  const [allVehicles, setAllVehicles] = useState([]);
  const [allInsurances, setAllInsurances] = useState([]);
  const [allContracts, setAllContracts] = useState([]);
  const [coverageModels, setCoverageModels] = useState([]);
  const [loading, setLoading] = useState(true);

  const [chosenContractId, setChosenContractId] = useState(fixedContract?.id || '');
  const chosenContract = fixedContract
    || allContracts.find(c => String(c.id) === String(chosenContractId))
    || null;
  const isSingleVehicle = SINGLE_VEHICLE_CONTRACT_TYPES.has(chosenContract?.contract_type);
  // A mono contract that already has an active VI is frozen for "add".
  // Swapping the covered vehicle mid-policy is a distinct flow (post-salon).
  const singleSlotTaken = useMemo(() => {
    if (!isSingleVehicle || !chosenContract) return false;
    return allInsurances.some(vi => !vi.removed_at && vi.contract_id === chosenContract.id);
  }, [isSingleVehicle, chosenContract, allInsurances]);

  const [selected, setSelected] = useState(new Set());
  const [coverage, setCoverage] = useState(null);
  const [effectDate, setEffectDate] = useState(new Date().toISOString().split('T')[0]);
  const [done, setDone] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sending, setSending] = useState(false);

  // Load catalog + global insurance state.
  // Always fetch contracts (even when fixedContract is passed) so we can
  // cross-join every VI to its parent contract.status — a VI on a terminated
  // contract must NOT count as insurance (see utils/insuranceStatus.js).
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [vRes, viRes, modelsRes, cRes] = await Promise.all([
          api.vehicle.getAll({ status: 'active' }),
          api.vehicleInsurance.getAll(),
          insuranceModuleAPI.getModels(),
          api.insurance.getAll(),
        ]);

        if (vRes?.ok) setAllVehicles(vRes.data || []);
        if (viRes?.ok) setAllInsurances(viRes.data || []);
        if (modelsRes?.ok && Array.isArray(modelsRes.data) && modelsRes.data.length > 0) {
          setCoverageModels(modelsRes.data.map(m => ({
            id: m.id,
            code: m.code,
            name: m.name,
            recommended: !!m.is_recommended,
          })));
        }
        if (cRes?.ok) setAllContracts(cRes.data || []);
      } catch (err) {
        console.error('InsuranceVehiclePicker load error:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [fixedContract]);

  const COVERAGES = coverageModels.length > 0 ? coverageModels : FALLBACK_COVERAGES;
  const usingModels = coverageModels.length > 0;

  // Contracts eligible for the "choose a contract" dropdown (only used when
  // no fixedContract is passed in). Rules:
  //  - status === 'active'
  //  - single-vehicle contracts hidden unless their slot is free
  //    (no active non-removed VI)
  // Hides the "mono" group entirely if no empty mono is available.
  const { fleetContracts, emptyMonoContracts } = useMemo(() => {
    const actives = allContracts.filter(c => c.status === 'active');
    const occupiedContractIds = new Set(
      allInsurances.filter(vi => !vi.removed_at).map(vi => vi.contract_id)
    );
    const fleet = [];
    const mono = [];
    for (const c of actives) {
      if (SINGLE_VEHICLE_CONTRACT_TYPES.has(c.contract_type)) {
        if (!occupiedContractIds.has(c.id)) mono.push(c);
      } else {
        fleet.push(c);
      }
    }
    return { fleetContracts: fleet, emptyMonoContracts: mono };
  }, [allContracts, allInsurances]);

  // "Truly insured" set: VI active (removed_at IS NULL) AND parent contract
  // status === 'active'. A VI on a terminated/expired/suspended contract is
  // a data leak, not insurance — those vehicles appear here as assignable.
  // (see utils/insuranceStatus.js). Vehicles moved to the local `done`
  // queue this session are also excluded.
  const uninsuredVehicles = useMemo(() => {
    const insuredIds = buildTrulyInsuredIds(allInsurances, allContracts);
    let list = allVehicles.filter(v => !insuredIds.has(v.id) && !done.some(d => d.id === v.id));
    if (searchQuery.trim().length > 0) {
      const q = searchQuery.toLowerCase();
      list = list.filter(v =>
        (v.license_plate || '').toLowerCase().includes(q) ||
        (v.make || '').toLowerCase().includes(q) ||
        (v.model || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [allVehicles, allInsurances, allContracts, done, searchQuery]);

  // Single-vehicle contracts: exclusive selection (1 at a time). Also refuse
  // selection once a vehicle is already queued in `done` — the slot is taken
  // for this session.
  const monoCapReached = isSingleVehicle && done.length >= 1;
  const toggleVehicle = id => {
    if (monoCapReached) return;
    if (isSingleVehicle) {
      setSelected(selected.has(id) ? new Set() : new Set([id]));
      return;
    }
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const canApply = !singleSlotTaken && !monoCapReached && selected.size > 0 && coverage && chosenContract;
  const canFinish = !singleSlotTaken && ((done.length > 0) || canApply);

  const handleApply = () => {
    if (!canApply) return;
    const cov = COVERAGES.find(c => c.id === coverage);
    const toMove = uninsuredVehicles.filter(v => selected.has(v.id));
    const added = toMove.map(v => ({
      ...v,
      coverage: coverageName(cov),
      coverageId: coverage,
      effectDate,
    }));
    setDone([...added, ...done]);
    setSelected(new Set());
  };

  const handleFinish = async () => {
    // Fold pending selection if user clicked Valider without clicking Ajouter.
    let pending = done;
    if (done.length === 0 && canApply) {
      const cov = COVERAGES.find(c => c.id === coverage);
      const toMove = uninsuredVehicles.filter(v => selected.has(v.id));
      pending = toMove.map(v => ({ ...v, coverage: coverageName(cov), coverageId: coverage, effectDate }));
    }
    if (pending.length === 0 || !chosenContract) return;

    setSending(true);
    try {
      for (const v of pending) {
        const payload = {
          vehicle_id: v.id,
          contract_id: chosenContract.id,
          assigned_at: v.effectDate,
          is_primary: true,
          premium_amount: 0,
        };
        // Canon: only coverage_model_id (FK). The deprecated legacy coverage
        // string was never read by the assign route anyway — dropped to stop
        // reviving the dual-schema. When no coverage models exist the vehicle
        // is assigned without one (set later via the canon UI).
        if (usingModels) payload.coverage_model_id = v.coverageId;
        await insuranceModuleAPI.assignVehicle(payload);
      }
      onMessage?.({
        type: 'success',
        text: t('insurance.picker.assignedToContract', {
          n: pending.length,
          contract: chosenContract.policy_number || '',
        }).trim(),
      });
      onSuccess?.();
      onClose?.();
    } catch (err) {
      console.error('InsuranceVehiclePicker submit error:', err);
      onMessage?.({ type: 'error', text: t('insurance.picker.assignFailed') });
    } finally {
      setSending(false);
    }
  };

  const headerTitle = chosenContract
    ? (isSingleVehicle ? t('insurance.picker.titleSingle') : t('insurance.picker.titleFleet'))
    : t('insurance.picker.titleNoContract');
  const headerSubtitle = chosenContract
    ? t('insurance.picker.subtitle', {
        product: chosenContract.product_name
          || (isSingleVehicle ? t('insurance.picker.singleContract') : t('insurance.picker.fleetContract')),
        policy: chosenContract.policy_number || '',
      })
    : t('insurance.picker.subtitleNoContract');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-[900px] h-[620px] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-5 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Plus className="w-5 h-5" />
              {headerTitle}
            </h2>
            <p className="text-blue-100 text-sm">{headerSubtitle}</p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {isSingleVehicle && !singleSlotTaken && !monoCapReached && (
          <div className="px-5 py-2 bg-blue-50 border-b border-blue-100 text-xs text-blue-800">
            {t('insurance.picker.singleHint')}
          </div>
        )}
        {isSingleVehicle && monoCapReached && (
          <div className="px-5 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-800">
            {t('insurance.picker.singleReadyHint')}
          </div>
        )}
        {singleSlotTaken && (
          <div className="px-5 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-800">
            {t('insurance.picker.singleSlotTakenHint')}
          </div>
        )}

        {/* 3-column grid: A × B = C */}
        <div className="flex-1 grid grid-cols-[1fr_240px_1fr] overflow-hidden min-h-0">
          {/* Column A: Uninsured vehicles */}
          <div className="flex flex-col border-r border-gray-200 min-h-0">
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                {t('insurance.picker.toInsure')}
                <span className="px-2 py-0.5 text-xs font-semibold bg-blue-100 text-blue-700 rounded-full">
                  {uninsuredVehicles.length}
                </span>
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">{t('insurance.picker.clickToSelect')}</p>
            </div>
            <div className="px-3 pt-2 pb-1">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder={t('insurance.picker.searchPlaceholder')}
                  className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-3 min-h-0">
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : uninsuredVehicles.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-400">
                  <Check className="w-8 h-8 mb-2 opacity-50" />
                  <p className="text-sm text-center">
                    {searchQuery
                      ? t('common.states.noResult')
                      : (allVehicles.length === 0 ? t('insurance.picker.noVehicle') : t('insurance.picker.allInsured'))}
                  </p>
                </div>
              ) : (
                uninsuredVehicles.map(v => (
                  <div
                    key={v.id}
                    onClick={() => toggleVehicle(v.id)}
                    className={`flex items-center justify-between p-3 mb-2 rounded-lg border-2 cursor-pointer transition-all ${
                      selected.has(v.id)
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300 bg-white'
                    }`}
                  >
                    <span className="font-semibold text-sm text-gray-800">{v.license_plate}</span>
                    <span className="text-xs text-gray-500 truncate ml-2">
                      {v.make} {v.model}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Column B: Options */}
          <div className="flex flex-col bg-gray-50 border-r border-gray-200 min-h-0">
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-100">
              <h3 className="text-sm font-semibold text-gray-800">{t('insurance.picker.options')}</h3>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-4 min-h-0">
              {!fixedContract && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{t('insurance.picker.contract')}</p>
                  {fleetContracts.length === 0 && emptyMonoContracts.length === 0 ? (
                    <div className="text-xs text-gray-500 italic px-2 py-3 bg-gray-100 rounded">
                      {t('insurance.picker.noActiveContract')}
                    </div>
                  ) : (
                    <select
                      value={chosenContractId}
                      onChange={e => setChosenContractId(e.target.value)}
                      className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">{t('insurance.picker.chooseContract')}</option>
                      {fleetContracts.length > 0 && (
                        <optgroup label={t('insurance.picker.fleetContracts')}>
                          {fleetContracts.map(c => (
                            <option key={c.id} value={c.id}>
                              {c.policy_number} — {c.insurer_name || ''}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {emptyMonoContracts.length > 0 && (
                        <optgroup label={t('insurance.picker.freeSingleContracts')}>
                          {emptyMonoContracts.map(c => (
                            <option key={c.id} value={c.id}>
                              {c.policy_number} — {c.insurer_name || ''}
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </select>
                  )}
                </div>
              )}

              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{t('insurance.picker.coverageLabel')}</p>
                {COVERAGES.map(c => (
                  <div
                    key={c.id}
                    onClick={() => setCoverage(c.id)}
                    className={`p-3 mb-2 rounded-lg border-2 cursor-pointer transition-all ${
                      coverage === c.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <span className="text-sm font-semibold text-gray-800">
                      {coverageName(c)}
                      {c.recommended && (
                        <span className="ml-2 px-1.5 py-0.5 text-[10px] font-semibold bg-green-100 text-green-700 rounded">
                          {t('insurance.picker.recommended')}
                        </span>
                      )}
                    </span>
                  </div>
                ))}
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">
                  {t('insurance.picker.startDate')}
                </label>
                <input
                  type="date"
                  value={effectDate}
                  onChange={e => setEffectDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

            </div>
          </div>

          {/* Column C: Done */}
          <div className="flex flex-col min-h-0">
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                {t('insurance.picker.ready')}
                <span className="px-2 py-0.5 text-xs font-semibold bg-green-100 text-green-700 rounded-full">
                  {done.length}
                </span>
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">{t('insurance.picker.toValidateBelow')}</p>
            </div>
            <div className="flex-1 overflow-y-auto p-3 min-h-0">
              {done.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-400">
                  <ListTodo className="w-8 h-8 mb-2 opacity-50" />
                  <p className="text-sm text-center">
                    {t('insurance.picker.readyEmpty')}
                  </p>
                </div>
              ) : (
                done.map(v => (
                  <div
                    key={v.id}
                    className="flex items-center gap-3 p-3 mb-2 rounded-lg bg-green-50 border border-green-200"
                  >
                    <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-800 truncate">{v.license_plate}</p>
                      <p className="text-xs text-green-700 truncate">{v.coverage}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          <span className="text-sm text-gray-500">
            {!chosenContract
              ? t('insurance.picker.footerChooseContract')
              : done.length > 0
              ? t('insurance.picker.footerReady', { n: done.length })
              : canApply
              ? t('insurance.picker.footerSelected', { n: selected.size })
              : t('insurance.picker.footerSelectVehicles')}
          </span>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              disabled={sending}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {t('common.actions.cancel')}
            </button>
            <button
              onClick={handleApply}
              disabled={!canApply || sending}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-1"
              title={t('insurance.picker.addSelectionTitle')}
            >
              {t('insurance.picker.addSelection', { n: selected.size })} <ChevronRight className="w-4 h-4" />
            </button>
            <button
              onClick={handleFinish}
              disabled={!canFinish || sending}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {sending ? t('common.states.saving') : t('insurance.picker.validate')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default InsuranceVehiclePicker;
